import { io } from "socket.io-client";

let socket;

// Get socket URL from environment - use VITE_WS_BASE_URL or fallback to API base URL
const getSocketUrl = () => {
    // Try VITE_WS_BASE_URL first (for WebSocket-specific URL)
    let wsBaseUrl = import.meta.env.VITE_WS_BASE_URL;
    
    // Fallback to VITE_API_BASE_URL if WS_BASE_URL is not set
    if (!wsBaseUrl) {
        wsBaseUrl = import.meta.env.VITE_API_BASE_URL;
    }
    
    // Final fallback to default development URL
    if (!wsBaseUrl) {
        wsBaseUrl = 'http://feelbetter-dev.it-mitra.com/api';
        console.warn('⚠️ Chat Socket: Using default WebSocket URL. Set VITE_WS_BASE_URL or VITE_API_BASE_URL in .env file.');
    }
    
    // Validate URL - ensure it's not empty and is a valid URL
    if (!wsBaseUrl || typeof wsBaseUrl !== 'string' || wsBaseUrl.trim() === '') {
        console.error('❌ Chat Socket: Invalid WebSocket URL. Cannot connect.');
        return null;
    }
    
    // Remove /api suffix if present and trim any whitespace
    let socketUrl = wsBaseUrl.trim().replace(/\/api\/?$/, '');
    
    // Ensure URL is valid - must start with http:// or https://
    if (!socketUrl.match(/^https?:\/\//)) {
        console.error('❌ Chat Socket: URL must start with http:// or https://. Got:', socketUrl);
        return null;
    }
    
    // Prevent connecting to localhost dev server (Vite runs on port 3000)
    if (socketUrl.includes('localhost:3000') || socketUrl.includes('127.0.0.1:3000')) {
        console.error('❌ Chat Socket: Cannot connect to Vite dev server. Please set VITE_WS_BASE_URL or VITE_API_BASE_URL to your WebSocket server URL.');
        return null;
    }
    
    console.log('🔌 Chat socket URL:', socketUrl);
    
    return socketUrl;
};

export const getSocket = () => {
    // Check if user is authenticated before creating socket
    const userId = localStorage.getItem('ID');
    const userRole = localStorage.getItem('Role');
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

    // Don't create socket if user is not authenticated
    if (!userId || !userRole || !isAuthenticated) {
        console.log('Socket: User not authenticated, returning null');
        return null;
    }

    if (!socket) {
        // Build query string based on role
        const query = {};
        if (userRole === 'doctor') {
            query.doctorId = userId;
        } else if (userRole === 'patient') {
            query.patientId = userId;
        } else if (userRole === 'admin') {
            // Admin now connects too (for the real-time admin dashboard). The
            // backend already reads adminId in its handshake for support rooms.
            query.adminId = userId;
        }

        const socketUrl = getSocketUrl();
        if (!socketUrl) {
            console.error('Socket: Cannot create socket, URL is null or invalid');
            return null;
        }
        
        // Additional validation - only block localhost:3000 (Vite dev server)
        // Allow same-origin connections for production servers
        try {
            const urlObj = new URL(socketUrl);
            const currentOrigin = window.location.origin;
            
            // Only block if trying to connect to localhost:3000 (Vite dev server)
            if (urlObj.origin === currentOrigin && 
                (currentOrigin.includes('localhost:3000') || currentOrigin.includes('127.0.0.1:3000'))) {
                console.error('❌ Socket: Cannot connect to Vite dev server. Please set VITE_WS_BASE_URL or VITE_API_BASE_URL to your WebSocket server URL.');
                return null;
            }
            
            // Allow same-origin connections for production (not localhost:3000)
            // Socket.IO can handle same-origin connections in production
        } catch (e) {
            console.error('❌ Socket: Invalid URL format:', socketUrl, e);
            return null;
        }
        
        console.log('Creating chat socket with URL:', socketUrl, 'and query:', query);

        socket = io(socketUrl, {
            transports: ["websocket", "polling"], // WebSocket first, fallback to polling
            upgrade: true,
            autoConnect: false, // We'll connect manually in components
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
            query: query, // Add query params for auto-initialization
            // Send the JWT on every (re)connection so the backend can verify the
            // socket (required for the secured dashboard events). Function form so
            // a refreshed access token is picked up on reconnect, not frozen at
            // creation time.
            auth: (cb) => cb({ token: localStorage.getItem('accessToken') || null }),
        });

        // Basic connection logging (components will add their own listeners)
        socket.on("connect", () => {
            console.log("Chat socket connected with query:", query);
        });

        socket.on("disconnect", (reason) => {
            console.log("Chat socket disconnected:", reason);
        });

        socket.on("connect_error", (err) => {
            console.error("Chat socket connection error:", err);
        });

        socket.on("error", (error) => {
            console.error("Chat socket error:", error);
        });
    }
    return socket;
};

export const disconnectSocket = () => {
    if (socket) {
        console.log('Disconnecting chat socket');
        socket.removeAllListeners();
        socket.disconnect();
        socket = null;
    }
};

// Listen for logout events to disconnect socket
if (typeof window !== 'undefined') {
    window.addEventListener('logout', () => {
        disconnectSocket();
    });
}